import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../api";
import "./LogViewer.scss";

const LogViewer = ({ role }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [logContent, setLogContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLevel, setFilterLevel] = useState("all");
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.logs.getFiles();
      if (response.data && response.data.success) {
        setFiles(response.data.files || []);
      } else {
        setError("Failed to load log files");
      }
    } catch (err) {
      setError(err.message || "Failed to load log files");
    } finally {
      setLoading(false);
    }
  };

  const loadLogContent = async (filename, page = 1, search = "", level = "all") => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.logs.getLogContent(filename, {
        page,
        lines_per_page: 1000,
        search,
        level,
      });
      if (response.data && response.data.success) {
        setLogContent(response.data.data);
      } else {
        setError("Failed to load log content");
      }
    } catch (err) {
      setError(err.message || "Failed to load log content");
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async (filename) => {
    try {
      const response = await api.logs.getStatistics(filename);
      if (response.data && response.data.success) {
        setStatistics(response.data.statistics);
      }
    } catch (err) {
      console.error("Failed to load statistics:", err);
    }
  };

  const handleFileSelect = (filename) => {
    setSelectedFile(filename);
    setCurrentPage(1);
    setSearchTerm("");
    setFilterLevel("all");
    setLogContent(null);
    setStatistics(null);
    loadLogContent(filename, 1, "", "all");
    loadStatistics(filename);
  };

  const handlePageChange = (newPage) => {
    if (selectedFile && newPage > 0 && (!logContent || newPage <= logContent.total_pages)) {
      setCurrentPage(newPage);
      loadLogContent(selectedFile, newPage, searchTerm, filterLevel);
    }
  };

  const handleSearch = () => {
    if (selectedFile) {
      setCurrentPage(1);
      loadLogContent(selectedFile, 1, searchTerm, filterLevel);
    }
  };

  const handleFilterChange = (level) => {
    setFilterLevel(level);
    setCurrentPage(1);
    loadLogContent(selectedFile, 1, searchTerm, level);
  };

  const handleDownload = async (filename) => {
    try {
      const response = await api.logs.downloadLog(filename);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Failed to download log file");
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const getLogLevelClass = (line) => {
    if (line.includes('.ERROR:') || line.includes('.CRITICAL:')) return 'log-error';
    if (line.includes('.WARNING:')) return 'log-warning';
    if (line.includes('.INFO:')) return 'log-info';
    if (line.includes('.DEBUG:')) return 'log-debug';
    return '';
  };

  return (
    <div className="log-viewer-container">
      <div className="log-viewer-header">
        <h1>{t("systemLogs") || "System Logs"}</h1>
        <button className="btn-refresh" onClick={loadFiles} disabled={loading}>
          {loading ? t("loading") || "Loading..." : t("refresh") || "Refresh"}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="log-viewer-content">
        <div className="log-files-sidebar">
          <h3>{t("logFiles") || "Log Files"}</h3>
          <div className="file-list">
            {files.length === 0 && !loading && (
              <p className="no-files">{t("noLogFiles") || "No log files found"}</p>
            )}
            {files.map((file) => (
              <div
                key={file.name}
                className={`file-item ${selectedFile === file.name ? 'active' : ''}`}
                onClick={() => handleFileSelect(file.name)}
              >
                <div className="file-name">{file.name}</div>
                <div className="file-info">
                  <span className="file-size">{formatBytes(file.size)}</span>
                  <span className="file-date">{formatDate(file.modified)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="log-content-area">
          {!selectedFile ? (
            <div className="no-selection">
              <p>{t("selectLogFile") || "Select a log file to view its content"}</p>
            </div>
          ) : (
            <>
              <div className="log-controls">
                <div className="search-bar">
                  <input
                    type="text"
                    placeholder={t("searchLogs") || "Search logs..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <button onClick={handleSearch} disabled={loading}>
                    {t("search") || "Search"}
                  </button>
                </div>

                <div className="filter-buttons">
                  <button
                    className={filterLevel === 'all' ? 'active' : ''}
                    onClick={() => handleFilterChange('all')}
                  >
                    {t("all") || "All"}
                  </button>
                  <button
                    className={filterLevel === 'error' ? 'active' : ''}
                    onClick={() => handleFilterChange('error')}
                  >
                    {t("errors") || "Errors"}
                  </button>
                  <button
                    className={filterLevel === 'warning' ? 'active' : ''}
                    onClick={() => handleFilterChange('warning')}
                  >
                    {t("warnings") || "Warnings"}
                  </button>
                  <button
                    className={filterLevel === 'info' ? 'active' : ''}
                    onClick={() => handleFilterChange('info')}
                  >
                    {t("info") || "Info"}
                  </button>
                </div>

                <button
                  className="btn-download"
                  onClick={() => handleDownload(selectedFile)}
                >
                  {t("download") || "Download"}
                </button>
              </div>

              {statistics && (
                <div className="log-statistics">
                  <h4>{t("statistics") || "Statistics"}</h4>
                  <div className="stats-grid">
                    {Object.entries(statistics.levels).map(([level, count]) => (
                      <div key={level} className="stat-item">
                        <span className="stat-label">{level}:</span>
                        <span className="stat-value">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loading ? (
                <div className="loading-indicator">
                  <p>{t("loading") || "Loading..."}</p>
                </div>
              ) : logContent ? (
                <>
                  <div className="log-lines">
                    {logContent.lines.length === 0 ? (
                      <p className="no-results">{t("noMatchingLines") || "No matching lines found"}</p>
                    ) : (
                      logContent.lines.map((line, index) => (
                        <div key={index} className={`log-line ${getLogLevelClass(line)}`}>
                          <span className="line-number">{(currentPage - 1) * 1000 + index + 1}</span>
                          <span className="line-content">{line}</span>
                        </div>
                      ))
                    )}
                  </div>

                  {logContent.total_pages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                      >
                        {t("previous") || "Previous"}
                      </button>
                      <span className="page-info">
                        {t("page") || "Page"} {currentPage} {t("of") || "of"} {logContent.total_pages}
                        {" "}({logContent.total_lines} {t("lines") || "lines"})
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= logContent.total_pages || loading}
                      >
                        {t("next") || "Next"}
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogViewer;


